import React, { Fragment, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import QRCode from "react-qr-code"

import { formatMoney, formatDate, regexPhoneNumber } from "helpers/helpers"
import logo from "assets/images/logo.png"
import "./ChequePreviews.css"

/*
 * Превью чеков в конструкторе шаблона — порт шаблонов кассы
 * (electron_mison_client/src/custom/chequeReceiptPrintTemplates.js).
 * Каждый вид чека (пречек, оплата, кухня, удаление, возврат, резерв) и тип заказа
 * (зал / с собой / доставка) рисуется отдельно, с теми же условиями показа, что на кассе.
 */

// ---------- настройки: groups конструктора → то же, что cheque_settings на кассе ----------

const FONT_PX = { small: "12px", medium: "14px", large: "16px" }
const CLIENT_PHONE_PX = { small: "18px", medium: "20px", large: "22px" }
const NUMBER_HEADER_PX = { small: "16px", medium: "24px", large: "30px" }
const NUMBER_DIGITS_PX = { small: "30px", medium: "56px", large: "72px" }

function fontPx(token, table = FONT_PX) {
	return table[String(token || "").toLowerCase().trim()] || table.medium
}

function alignToJustify(align) {
	if (align === "center") return "center"
	if (align === "right") return "flex-end"
	return "space-between"
}

function fieldShown(field) {
	if (field.input_type === "font_size") return field.is_show !== false
	if (field.is_show !== undefined && field.is_show !== null) return !!field.is_show
	if (field.input_type === "boolean" || field.input_type === "textarea") {
		return field.default_is_show != null ? !!field.default_is_show : true
	}
	return false
}

/** Плоские флаги и тексты, как getMergedFlatChequeSettings на кассе. */
function buildFlat(groups) {
	const flat = {}
	for (const g of groups || []) {
		for (const f of g.fields || []) {
			if (!f.field_key || f.field_key.endsWith("_products_font")) continue
			flat[f.field_key] = fieldShown(f)
			if (f.field_key === "show_promo_text_pre_cheque") flat.promo_text_pre_cheque = f.text_value || null
			if (f.field_key === "show_promo_text_post_cheque") flat.promo_text_post_cheque = f.text_value || null
			if (f.field_key === "show_loyalty_qr") flat.loyalty_qr = f.text_value || null
		}
	}
	return {
		...flat,
		show_order_type: flat.show_order_type ?? flat.show_order_type_name,
		kitchen_order_type: flat.kitchen_order_type ?? flat.kitchen_order_type_name,
	}
}

/** Шрифты и выравнивание полей группы: { styles, table, def }. */
function buildTypography(groups, groupValue, tableKey) {
	const g = (groups || []).find((x) => x.value === groupValue)
	const styles = {}
	for (const f of g?.fields || []) {
		if (!f.field_key || f.field_key === "show_cheque_number") continue
		const entry = {
			fontSize: f.field_key === "show_client_phone"
				? fontPx(f.has_font_size === false ? undefined : f.font_size, CLIENT_PHONE_PX)
				: fontPx(f.has_font_size === false ? undefined : f.font_size),
		}
		if (f.has_align === true) {
			entry.textAlign = ["center", "right", "left"].includes(f.align) ? f.align : "left"
		}
		styles[f.field_key] = entry
	}
	const tableField = tableKey
		? (groups || []).flatMap((x) => x.fields || []).find((x) => x.field_key === tableKey)
		: null
	return {
		styles,
		table: tableField?.font_size ? fontPx(tableField.font_size) : FONT_PX.medium,
		def: { fontSize: FONT_PX.medium, textAlign: "left" },
	}
}

function fieldTypo(typo, ...keys) {
	for (const k of keys) if (k && typo.styles[k]) return typo.styles[k]
	return typo.def
}

function flexRowStyle(typo, ...keys) {
	const st = fieldTypo(typo, ...keys)
	return {
		fontSize: st.fontSize,
		display: "flex",
		justifyContent: alignToJustify(st.textAlign),
		width: "100%",
		alignItems: "baseline",
	}
}

function blockStyle(typo, ...keys) {
	const st = fieldTypo(typo, ...keys)
	return { fontSize: st.fontSize, textAlign: st.textAlign, width: "100%" }
}

function numberFonts(groups, groupValue, fieldKey) {
	const g = (groups || []).find((x) => x.value === groupValue)
	const f = (g?.fields || []).find((x) => x.field_key === fieldKey)
	const token = ["small", "medium", "large"].includes(f?.font_size) ? f.font_size : "medium"
	return { header: NUMBER_HEADER_PX[token], digits: NUMBER_DIGITS_PX[token] }
}

// ---------- демо-данные ----------

const ORDER_HALL = 1
const ORDER_TAKEAWAY = 2
const ORDER_DELIVERY = 3

function money(v) {
	return formatMoney(v)
}

/** Демо-чек: stage = 'precheck' (гость ещё не платил) или 'payment' (чек оплаты). */
function buildCheque(orderType, stage) {
	const hall = orderType === ORDER_HALL
	const delivery = orderType === ORDER_DELIVERY
	const paid = stage === "payment"
	const servicePct = hall ? 10 : 0
	const products = [
		{ name: "Big Burger", quantity: 2, sale_price: 50000, vat: 12, fiscal_code: "02009001006000000", service_percentage: servicePct, service_amount: hall ? 10000 : 0 },
		{ name: "Сыр чеддер", quantity: 2, modifier: true },
		{ name: "Olivie salat", quantity: 1, sale_price: 30000, vat: 12, fiscal_code: "10202001001000001", service_percentage: servicePct, service_amount: hall ? 3000 : 0 },
	]
	const totalAmount = 130000
	const serviceAmount = hall ? 13000 : 0
	const deliveryPrice = delivery ? 15000 : 0
	const toPay = totalAmount + serviceAmount + deliveryPrice
	const payments = []
	if (paid) {
		payments.push({ payment_type_name: "Наличка", amount: toPay + 7000 })
		payments.push({ transaction_type: "minus", payment_purpose_id: 2, amount: 7000 })
	}
	return {
		order_type: orderType,
		cheque_number: 27,
		cheque_opened_at: "2025-05-27 15:52:56",
		cheque_closed_at: paid ? "2025-05-27 16:41:10" : null,
		pos_name: "POS 1",
		pos_phone: "998998132213",
		cashier_first_name: "Dilshod",
		waiter_first_name: "Dilshod",
		people_number: 2,
		hall_name: "Asosiy zal",
		table_name: "VIP 2",
		client_name: hall ? null : "Иван Петров",
		client_phone: hall ? null : "+998 90 123 45 67",
		payment_type_id: delivery ? 1 : null,
		payment_type_name: "Наличка",
		client_address: delivery ? "ул. Навои, 15" : null,
		navigate: delivery ? "Напротив банка" : null,
		apartment: delivery ? "24" : null,
		floor: delivery ? "3" : null,
		entrance: delivery ? "2" : null,
		intercom: delivery ? "24" : null,
		delivery_price: deliveryPrice,
		service_percentage: servicePct,
		service_amount: serviceAmount,
		total_amount: totalAmount,
		total_amount_service: toPay,
		total_vat_amount: 13929,
		paid: paid ? toPay : 0,
		payments,
		note: "Без лука",
		external_name: delivery ? "Яндекс Еда" : null,
		external_id: delivery ? "190332-1234568" : null,
		fiscal_terminal_id: paid ? "LG420230652808" : null,
		fiscal_sign: paid ? "952151270351" : null,
		fiscal_qr_code: paid ? "https://ofd.soliq.uz/check?t=LG420230652808&r=94&c=20250531170130&s=952151270351" : null,
		products,
	}
}

function buildKitchenData(orderType) {
	return {
		order_type: orderType,
		cheque_number: 27,
		hall_name: "Asosiy zal",
		table_name: "VIP 2",
		employee_name: "Dilshod",
		created_by: "Dilshod",
		deleted_by: "Alisher",
		comment: "Меньше майонеза",
		note: "Оставить под дверью",
		external_name: orderType === ORDER_DELIVERY ? "Яндекс Еда" : null,
		external_id: orderType === ORDER_DELIVERY ? "190332-1234568" : null,
		deletion_reason_name: "Ошибка официанта",
		deletion_reason_comment: "Гость передумал",
	}
}

const KITCHEN_PRODUCTS = [
	{ name: "Big Burger", quantity: 2, sale_price: 50000, comment: "Без лука", ingredients: [{ name: "Огурцы", quantity: 1, del: true }, { name: "Сыр", quantity: 1 }], printIngredients: true },
	{ name: "Сыр чеддер", quantity: 2, modifier: true, sale_price: 0 },
	{ name: "Olivie salat", quantity: 1, sale_price: 30000 },
]

const KITCHEN_CHANGED_PRODUCTS = [
	{ name: "Big Burger", old_quantity: 3, new_quantity: 2 },
	{ name: "Сыр чеддер", modifier: true, old_quantity: 3, new_quantity: 2 },
	{ name: "Olivie salat", old_quantity: 1, new_quantity: 0 },
]

const RESERVATION_DATA = {
	hall_name: "Asosiy zal",
	table_name: "VIP 2",
	reservation_date: "2026-01-21",
	reservation_start_time: "19:00",
	reservation_end_time: "21:00",
	people_number: 4,
	client_name: "Иван Петров",
	client_phone: "+998555000089",
	amount: 125000,
	employee_name: "Шохрух",
	pos_phone: "+998712000000",
	products: [
		{ name: "Big Burger", quantity: 2 },
		{ name: "Olivie salat", quantity: 1 },
	],
}

const hasText = (v) => v != null && String(v).trim() !== ""

// ---------- общие куски ----------

function RowNumber(flat, index, lines) {
	if (flat.show_row_number === false) return ""
	if (!Array.isArray(lines)) return `${index + 1}. `
	if (lines[index]?.modifier) return ""
	return `${lines.slice(0, index).filter((x) => !x.modifier).length + 1}. `
}

function PersonSplit({ total }) {
	const items = []
	for (let n = 2; n <= 17; n++) items.push(`${money(total)}/${n}=${money(Math.floor(total / n))}`)
	const mid = Math.ceil(items.length / 2)
	const left = items.slice(0, mid)
	const right = items.slice(mid)
	return (
		<div style={{ fontFamily: "ui-monospace, Menlo, Consolas, monospace", fontSize: 12, lineHeight: 1.35, marginTop: 6, marginBottom: 8 }}>
			{left.map((line, i) => (
				<div key={i} style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", columnGap: 10 }}>
					<span style={{ whiteSpace: "nowrap" }}>{line}</span>
					<span>|</span>
					<span style={{ whiteSpace: "nowrap" }}>{right[i] ?? ""}</span>
				</div>
			))}
		</div>
	)
}

function NumberBlock({ fonts, number, label, style }) {
	return (
		<>
			<div style={{ width: "100%", textAlign: "center", lineHeight: 1.1, ...style }}>
				<b style={{ fontSize: fonts.header }}>{label}</b>
			</div>
			<div style={{ width: "100%", textAlign: "center", lineHeight: 1.05, marginTop: 4 }}>
				<b style={{ fontSize: fonts.digits }}>{number}</b>
			</div>
		</>
	)
}

// ---------- чек гостя: пречек и оплата ----------

function ChequeReceipt({ stage, orderType, groups, t }) {
	const isPayment = stage === "payment"
	const c = buildCheque(orderType, stage)
	const flat = buildFlat(groups)
	const typo = buildTypography(groups, "cheque", "cheque_products_font")
	const fonts = numberFonts(groups, "cheque", "show_cheque_number")
	const showPeople = Number(c.people_number) > 0 && flat.show_people_number !== false
	const hasRoomService = !!c.service_percentage && c.service_amount > 0
	const toPay = isPayment ? money(c.total_amount_service) : money(c.total_amount_service - c.paid)
	const feedbackOn = isPayment && flat.show_feedback_qr
	const vatOf = (item) => money(((item.sale_price * item.quantity - 0) * item.vat) / (100 + item.vat))

	const rows = c.products.map((item, index, list) => {
		const isFree = item.modifier
		const lineTotal = isFree ? 0 : item.quantity * item.sale_price
		const prefix = RowNumber(flat, index, list)
		const qtyCell = isFree ? String(item.quantity) : `${item.quantity}x${money(item.sale_price)}`
		const showService = flat.is_service_charge_visible && Number(item.service_percentage) > 0
		return (
			<Fragment key={index}>
				<tr>
					<td>{item.modifier ? <em style={{ marginLeft: 10 }}>{prefix}{item.name}</em> : <b>{prefix}{item.name}</b>}</td>
					<td className="text-end text-nowrap"><b>{qtyCell}</b></td>
					<td className="text-end text-nowrap"><b>{isFree ? "" : money(lineTotal)}</b></td>
				</tr>
				{showService && (isPayment ? (
					<tr>
						<td className="ps-3">{t("service")} {money(item.service_percentage)}%</td>
						<td className="text-end" colSpan={2}>{money(item.service_amount)}</td>
					</tr>
				) : (
					<tr><td className="ps-3" colSpan={3}>{t("service")} {money(item.service_percentage)}%: {money(item.service_amount)}</td></tr>
				))}
				{flat.show_vat && Number(item.vat) > 0 && (isPayment ? (
					<tr>
						<td className="ps-3">{t("vat")} {money(item.vat)}%</td>
						<td className="text-end" colSpan={2}>{vatOf(item)}</td>
					</tr>
				) : (
					<tr><td className="ps-3" colSpan={3}>{t("vat")} {money(item.vat)}%: {vatOf(item)}</td></tr>
				))}
				{flat.show_fiscal_code && !!item.fiscal_code && (isPayment ? (
					<tr>
						<td className="ps-3">{t("mxik")}</td>
						<td className="text-end" colSpan={2}>{item.fiscal_code}</td>
					</tr>
				) : (
					<tr><td className="ps-3" colSpan={3}>{t("mxik")}: {item.fiscal_code}</td></tr>
				))}
			</Fragment>
		)
	})

	return (
		<div className="cheque-paper">
			<div className="d-flex flex-column w-100 mb-2">
				{flat.show_logo && (
					<div className="d-flex justify-content-center mb-2" style={blockStyle(typo, "show_logo")}>
						<img src={logo} width={128} alt="logo" />
					</div>
				)}
				{flat.show_pos_name && (
					<h3 className={fieldTypo(typo, "show_pos_name").textAlign === "center" ? "text-center" : "text-start"}
						style={{ fontSize: fieldTypo(typo, "show_pos_name").fontSize, margin: 0 }}>
						{c.pos_name}
					</h3>
				)}
				{flat.show_pos_phone && (
					<div style={flexRowStyle(typo, "show_pos_phone")}><p className="fw-600">{t("phone")}:</p><p>{c.pos_phone}</p></div>
				)}
				{flat.show_cheque_opened_at && (
					<div style={flexRowStyle(typo, "show_cheque_opened_at")}><p className="fw-600">{t("open_date")}:</p><p>{formatDate(c.cheque_opened_at, "dd.MM.yy HH:mm")}</p></div>
				)}
				{flat.show_cheque_printed_at && (
					<div style={flexRowStyle(typo, "show_cheque_printed_at")}><p className="fw-600">{t("print_date")}:</p><p>{formatDate(c.cheque_closed_at || c.cheque_opened_at, "dd.MM.yy HH:mm")}</p></div>
				)}
				{flat.show_cheque_closed_at && !!c.cheque_closed_at && (
					<div style={flexRowStyle(typo, "show_cheque_closed_at")}><p className="fw-600">{t("close_date")}:</p><p>{formatDate(c.cheque_closed_at, "dd.MM.yy HH:mm")}</p></div>
				)}
				{orderType === ORDER_HALL && (
					<>
						{flat.show_hall_name && !!c.hall_name && (
							<div style={flexRowStyle(typo, "show_hall_name")}><p className="fw-600">{t("hall")}:</p><p>{c.hall_name}</p></div>
						)}
						{flat.show_table_name && !!c.table_name && (
							<div style={flexRowStyle(typo, "show_table_name")}><p className="fw-600">{t("table")}:</p><p>{c.table_name}</p></div>
						)}
					</>
				)}
				{orderType !== ORDER_HALL && flat.show_order_type && (
					<div style={flexRowStyle(typo, "show_order_type_name", "show_order_type")}>
						<p className="fw-600">{t("order")}:</p>
						<p>{orderType === ORDER_TAKEAWAY ? t("takeaway") : t("delivery")}</p>
					</div>
				)}
				{flat.show_cashier_name && !!c.cashier_first_name && (
					<div style={flexRowStyle(typo, "show_cashier_name")}><p className="fw-600">{t("cashier")}:</p><span>{c.cashier_first_name}</span></div>
				)}
				{flat.show_waiter_name && !!c.waiter_first_name && (
					<div style={flexRowStyle(typo, "show_waiter_name")}><p className="fw-600">{t("employee")}:</p><span>{c.waiter_first_name}</span></div>
				)}
				{showPeople && (
					<div style={flexRowStyle(typo, "show_people_number")}><p className="fw-600">{t("guests")}:</p><span>{c.people_number}</span></div>
				)}
				{orderType !== ORDER_HALL && (
					<>
						{orderType === ORDER_DELIVERY && !!c.payment_type_id && (
							<div style={flexRowStyle(typo, "show_payment_type_name")}><p className="fw-600">{t("payment")}:</p><span>{c.payment_type_name}</span></div>
						)}
						{c.client_name && <div style={{ fontSize: fieldTypo(typo, "show_client_name").fontSize }}>{c.client_name}</div>}
						{c.client_phone && <div style={{ fontSize: fieldTypo(typo, "show_client_phone").fontSize }}><b>{c.client_phone}</b></div>}
					</>
				)}
				{orderType === ORDER_DELIVERY && (
					<>
						{hasText(c.client_address) && (
							<div style={{ fontSize: fieldTypo(typo, "show_client_address").fontSize, whiteSpace: "normal", wordBreak: "break-word" }}>
								<b>{t("address")}: {c.client_address}</b>
							</div>
						)}
						{hasText(c.navigate) && (
							<div style={{ fontSize: fieldTypo(typo, "show_client_address").fontSize, whiteSpace: "normal", wordBreak: "break-word" }}>
								<b>{t("navigate")}: {c.navigate}</b>
							</div>
						)}
						{(hasText(c.apartment) || hasText(c.floor)) && (
							<div className="d-flex gap-2" style={{ fontSize: 12 }}>
								{hasText(c.apartment) && <div className="d-flex justify-content-between" style={{ width: 110 }}><b>{t("apartment")}:</b><span>{c.apartment}</span></div>}
								{hasText(c.floor) && <div className="d-flex justify-content-between" style={{ width: 110 }}><b>{t("floor")}:</b><span>{c.floor}</span></div>}
							</div>
						)}
						{(hasText(c.entrance) || hasText(c.intercom)) && (
							<div className="d-flex gap-2" style={{ fontSize: 12 }}>
								{hasText(c.entrance) && <div className="d-flex justify-content-between" style={{ width: 110 }}><b>{t("entrance")}:</b><span>{c.entrance}</span></div>}
								{hasText(c.intercom) && <div className="d-flex justify-content-between" style={{ width: 110 }}><b>{t("intercom")}:</b><span>{c.intercom}</span></div>}
							</div>
						)}
					</>
				)}
			</div>

			<div className="cheque-block-2">
				<table className="custom-cheque-table w-100" style={{ fontSize: typo.table }}>
					<thead>
						<tr>
							<th className="text-start w-50">{flat.show_row_number === false ? t("name") : `# ${t("name")}`}</th>
							<th className="text-end text-nowrap w-25">{t("quantity")}</th>
							<th className="text-end w-25">{t("amount")}</th>
						</tr>
					</thead>
					<tbody>{rows}</tbody>
				</table>
			</div>

			<div className="cheque-block-3">
				<div className="custom-cheque-info"><div>{t("amount")}:</div><div>{money(c.total_amount)}</div></div>
				{flat.show_total_quantity === true && (
					<div className="custom-cheque-info"><div>{t("total_quantity")}:</div><div>3</div></div>
				)}
				{isPayment && flat.show_vat && c.total_vat_amount > 0 && (
					<div className="custom-cheque-info"><div>{t("vat_amount")}:</div><div>{money(c.total_vat_amount)}</div></div>
				)}
				{!!c.delivery_price && (
					<div className="custom-cheque-info"><div>{t("delivery")}:</div><div>{money(c.delivery_price)}</div></div>
				)}
				{hasRoomService && (
					<div className="custom-cheque-info"><div>{t("room_service")}: {c.service_percentage}%</div><div>{money(c.service_amount)}</div></div>
				)}
			</div>

			<div className="custom-cheque-info fz18" style={{ fontWeight: 700, marginBottom: isPayment ? undefined : 0 }}>
				<b>{t("to_pay")}:</b>
				<b>{toPay}</b>
			</div>

			<div style={isPayment ? undefined : { display: "flex", flexDirection: "column", gap: 6 }}>
				{Number(c.paid) > 0 && (
					<div className="custom-cheque-info"><div>{t("paid")}:</div><div>{money(c.paid)}</div></div>
				)}

				{isPayment && (
					<>
						<div className="brb-dashed"></div>
						<div className="cheque-payments" style={{ marginBottom: 16 }}>
							{c.payments.map((p, i) => {
								const isChange = p.payment_purpose_id === 2 || p.transaction_type === "minus"
								return (
									<div className="custom-cheque-info brb-dashed" key={i}>
										<div className="w-100 d-flex justify-content-between">
											<span>{isChange ? `${t("change")}:` : `${p.payment_type_name}:`}</span>
											<div className="text-nowrap">{money(p.amount)}</div>
										</div>
									</div>
								)
							})}
						</div>
					</>
				)}

				{flat.is_split_by_persons && orderType === ORDER_HALL && <PersonSplit total={c.total_amount_service} />}

				{c.external_name && (
					<div className="d-flex justify-content-between" style={{ fontSize: fieldTypo(typo, "show_external_info").fontSize, width: "100%" }}>
						<b>{t("service_name")}:</b><span>{c.external_name}</span>
					</div>
				)}
				{c.external_id && (
					<div className="d-flex justify-content-between" style={{ fontSize: fieldTypo(typo, "show_external_info").fontSize, width: "100%" }}>
						<b>{t("service_name")} ID:</b><span>{c.external_id}</span>
					</div>
				)}
				{hasText(c.note) && (
					<div className="d-flex" style={{ fontSize: fieldTypo(typo, "show_cheque_comment").fontSize }}>
						<span>{t("note")}:</span><span>{c.note}</span>
					</div>
				)}

				{isPayment && c.fiscal_terminal_id && (
					<div className="d-flex justify-content-between"><p>{t("terminal")}</p><p>{c.fiscal_terminal_id}</p></div>
				)}
				{isPayment && c.fiscal_sign && (
					<>
						<div className="d-flex justify-content-between"><p>{t("fiscal_sign")}</p><p>{c.fiscal_sign}</p></div>
						<div className="d-flex justify-content-center"><QRCode value={c.fiscal_qr_code} size={140} /></div>
					</>
				)}

				{/* QR оплаты Rahmat — только на пречеке, пока гость не заплатил */}
				{!isPayment && !c.external_id && (
					<>
						<div className="brb-dashed" />
						<div className="text-center"><b>RAHMAT - {t("payment_and_tips")}</b></div>
						<div className="d-flex justify-content-center"><QRCode value="https://app.rhmt.uz/p/mison/1/1" size={100} /></div>
					</>
				)}

				{/* QR отзыва и кэшбэка — только на чеке оплаты */}
				{feedbackOn && (
					<div className="d-flex justify-content-center pt-1 mb-3" style={blockStyle(typo, "show_feedback_qr")}>
						<QRCode size={100} value="https://service.mison.uz/feedback/1/1/demo" />
					</div>
				)}
				{isPayment && flat.show_loyalty_qr && (
					<div className="d-flex flex-column align-items-center pt-1 mb-3" style={blockStyle(typo, "show_loyalty_qr")}>
						<QRCode size={100} value="https://service.mison.uz/cashback/1/demo" />
						{flat.loyalty_qr && <p style={{ textAlign: "center", marginTop: 4 }}>{flat.loyalty_qr}</p>}
					</div>
				)}

				{flat.show_cheque_number && <NumberBlock fonts={fonts} number={c.cheque_number} label={t("order_number")} style={isPayment ? { marginTop: 8 } : undefined} />}

				{!isPayment && flat.show_promo_text_pre_cheque && flat.promo_text_pre_cheque && (
					<div className="d-flex justify-content-center" style={{ ...blockStyle(typo, "show_promo_text_pre_cheque"), marginTop: flat.show_cheque_number ? 14 : undefined }}>
						<p>{flat.promo_text_pre_cheque}</p>
					</div>
				)}
				{isPayment && flat.show_promo_text_post_cheque && flat.promo_text_post_cheque && (
					<div className="d-flex justify-content-center" style={{ ...blockStyle(typo, "show_promo_text_post_cheque"), marginTop: flat.show_cheque_number ? 14 : undefined }}>
						<p>{flat.promo_text_post_cheque}</p>
					</div>
				)}
			</div>
		</div>
	)
}

// ---------- чек возврата ----------

function ReturnReceipt({ t }) {
	return (
		<div className="cheque-paper" style={{ fontSize: 14 }}>
			{/* Шапка как у пречека: логотип, название по центру, телефон и адрес строками «подпись — значение» */}
			<div className="d-flex flex-column w-100">
				<div className="d-flex justify-content-center mb-2"><img src={logo} alt="" width={128} /></div>
				<h3 className="text-center" style={{ fontSize: 14, margin: 0 }}>POS 1</h3>
				<div className="d-flex justify-content-between w-100" style={{ alignItems: "baseline" }}><p className="fw-600">{t("phone")}:</p><p>998998132213</p></div>
				<div className="d-flex justify-content-between w-100" style={{ alignItems: "baseline" }}><p className="fw-600">{t("address")}:</p><p className="text-end">Toshkent</p></div>
				<div className="text-center" style={{ marginTop: 6, fontSize: 18 }}><b>{t("return_cheque")}</b></div>
			</div>
			<div className="brb-dashed" style={{ margin: "6px 0" }}></div>
			<div className="d-flex justify-content-between"><span>{t("return_cheque")} №</span><b>R-104</b></div>
			<div className="d-flex justify-content-between"><span>{t("cheque")} №</span><b>27</b></div>
			<div className="d-flex justify-content-between"><span>{t("date")}</span><span>27.05.25 17:10</span></div>
			<div className="d-flex justify-content-between"><span>{t("cashier")}</span><span>Dilshod</span></div>
			<div className="brb-dashed" style={{ margin: "6px 0" }}></div>
			<table style={{ width: "100%" }}>
				<tbody>
					<tr>
						<td style={{ verticalAlign: "top" }}>1. Big Burger</td>
						<td style={{ verticalAlign: "top", whiteSpace: "nowrap" }}>1 X {money(50000)}</td>
						<td style={{ verticalAlign: "top", whiteSpace: "nowrap", textAlign: "right" }}>{money(50000)}</td>
					</tr>
				</tbody>
			</table>
			<div className="brb-dashed" style={{ margin: "6px 0" }}></div>
			<div className="d-flex justify-content-between"><span>{t("service")}</span><span>{money(5000)}</span></div>
			<div className="d-flex justify-content-between" style={{ fontSize: 18 }}><b>{t("amount_to_refund")}</b><b>{money(55000)}</b></div>
			<div className="d-flex justify-content-between"><span>{t("cash")}</span><span>{money(55000)}</span></div>
			<div className="brb-dashed" style={{ margin: "6px 0" }}></div>
			<div className="d-flex justify-content-between fz14"><span>{t("terminal")}</span><span>LG420230652808</span></div>
			<div className="d-flex justify-content-between fz14"><span>{t("fiscal_sign")}</span><span>952151270351</span></div>
			<div className="d-flex justify-content-center" style={{ paddingTop: 6 }}>
				<QRCode value="https://ofd.soliq.uz/check?t=LG420230652808&r=94" size={100} />
			</div>
		</div>
	)
}

// ---------- кухня ----------

const KITCHEN_QTY_COL = { flex: "0 0 auto", width: "3.6em", textAlign: "center", whiteSpace: "nowrap" }
const KITCHEN_SUM_COL = { flex: "0 0 auto", textAlign: "right", whiteSpace: "nowrap", marginRight: "0.8em" }

function kitchenHeader({ flat, typo, t, orderType, title, titleStyle, withTable }) {
	const d = buildKitchenData(orderType)
	const orderTypeStyle = blockStyle(typo, "kitchen_order_type_name", "kitchen_order_type")
	return (
		<>
			<div className="text-center" style={{ width: "100%", ...titleStyle }}>
				<b>{title}</b>
				{!withTable && <br />}
				{!withTable && flat.kitchen_date && <b>{formatDate("2025-05-27 15:52:56", "dd.MM.yyyy HH:mm")}</b>}
			</div>
			{withTable && flat.kitchen_date && (
				<div className="text-center" style={blockStyle(typo, "kitchen_date")}><b>{formatDate("2025-05-27 15:52:56", "dd.MM.yyyy HH:mm")}</b></div>
			)}
			{flat.kitchen_order_type && (
				<div className="text-center" style={orderTypeStyle}>
					<b>
						{orderType === ORDER_HALL
							? (withTable ? `${d.hall_name} · ${d.table_name}` : t("hall"))
							: orderType === ORDER_TAKEAWAY ? t("takeaway") : t("delivery")}
					</b>
				</div>
			)}
		</>
	)
}

function KitchenNewOrder({ orderType, groups, t }) {
	const flat = buildFlat(groups)
	const typo = buildTypography(groups, "kitchen", "kitchen_products_font")
	const fonts = numberFonts(groups, "kitchen", "kitchen_cheque_number")
	const d = buildKitchenData(orderType)
	const showRowNumber = flat.kitchen_row_number !== false
	const newOrderStyle = { ...blockStyle(typo, "kitchen_new_order", "kitchen_date", "kitchen_order_type_name", "kitchen_order_type") }
	newOrderStyle.fontSize = `${Math.round((parseInt(newOrderStyle.fontSize, 10) || 14) * 1.5)}px`
	const products = KITCHEN_PRODUCTS

	return (
		<div className="cheque-paper">
			{kitchenHeader({ flat, typo, t, orderType, title: t("new_order"), titleStyle: newOrderStyle, withTable: true })}

			<div style={{ fontSize: typo.table, width: "100%" }}>
				<div style={{ width: "100%", borderTop: "2px solid #000", margin: "2px 0" }} />
				<div className="d-flex">
					<b style={{ flex: "1 1 auto", minWidth: 0 }}>{showRowNumber ? `# ${t("name")}` : t("name")}</b>
					{flat.kitchen_sale_price && <b style={KITCHEN_SUM_COL}>{t("amount")}</b>}
					<b style={KITCHEN_QTY_COL}>{t("quantity")}</b>
				</div>
				{products.map((p, i) => {
					const mainNumber = products.slice(0, i + 1).filter((x) => !x.modifier).length
					return (
						<Fragment key={i}>
							<div className="d-flex" style={{ alignItems: "flex-start", marginTop: i > 0 ? (!p.modifier && products[i - 1].modifier ? 10 : 3) : 0 }}>
								<div className="text-start" style={{ flex: "1 1 auto", minWidth: 0, paddingRight: 8, wordBreak: "break-word", whiteSpace: "normal" }}>
									{!p.modifier && <span>{showRowNumber && `${mainNumber}. `}{p.name}</span>}
									{p.modifier && <em style={{ marginLeft: 10 }}>{p.name}</em>}
									{hasText(p.comment) && <div><b>!!!{p.comment}</b></div>}
								</div>
								{flat.kitchen_sale_price && <div style={KITCHEN_SUM_COL}>{money(p.quantity * (p.sale_price || 0))}</div>}
								<div style={KITCHEN_QTY_COL}>{p.quantity}</div>
							</div>
							{p.printIngredients && p.ingredients.map((ing, k) => (
								<div key={k} className="d-flex" style={{ alignItems: "flex-start" }}>
									<em className={ing.del ? "del" : ""} style={{ marginLeft: 10, fontSize: "0.9em", flex: "1 1 auto", minWidth: 0, paddingRight: 8 }}>
										{ing.del ? ing.name : `- ${ing.name}`}
									</em>
									{!ing.del && <span style={KITCHEN_QTY_COL}>{ing.quantity * p.quantity}</span>}
								</div>
							))}
						</Fragment>
					)
				})}
				<div style={{ width: "100%", borderTop: "2px solid #000", margin: "2px 0" }} />
			</div>

			{flat.kitchen_table_info && orderType === ORDER_HALL && !(flat.kitchen_order_type && d.table_name) && (
				<div style={flexRowStyle(typo, "kitchen_table_info")}><div>{t("hall")}: {d.hall_name}</div><div>{t("table")}: {d.table_name}</div></div>
			)}
			{flat.kitchen_employee_info && d.employee_name && (
				<div style={blockStyle(typo, "kitchen_employee_info")}><b>{t("employee")}:</b> {d.employee_name}</div>
			)}
			{hasText(d.comment) && (
				<div style={{ fontSize: fieldTypo(typo, "kitchen_comment").fontSize }}><div><b>{t("kitchen_comment")}:</b> {d.comment}</div></div>
			)}
			{hasText(d.note) && (
				<div style={{ fontSize: fieldTypo(typo, "cheque_comment").fontSize }}><div><b>{t("cheque_comment")}:</b> {d.note}</div></div>
			)}
			{flat.kitchen_external_info && d.external_name && (
				<>
					<div className="d-flex justify-content-between" style={{ ...flexRowStyle(typo, "kitchen_external_info"), justifyContent: "space-between" }}>
						<b>{t("service_name")}:</b><span>{d.external_name}</span>
					</div>
					<div className="d-flex justify-content-between" style={{ ...flexRowStyle(typo, "kitchen_external_info"), justifyContent: "space-between" }}>
						<b>{t("service_name")} ID:</b><span>{d.external_id}</span>
					</div>
				</>
			)}
			{flat.kitchen_cheque_number && (
				<div style={{ width: "100%", textAlign: "center", marginTop: 4 }}>
					<div><b style={{ fontSize: fonts.header }}>{t("order_number")}</b></div>
					<div><b style={{ fontSize: fonts.digits }}>{d.cheque_number}</b></div>
				</div>
			)}
		</div>
	)
}

function KitchenDeleted({ kind, orderType, groups, t }) {
	const flat = buildFlat(groups)
	const typo = buildTypography(groups, "kitchen", "kitchen_products_font")
	const d = buildKitchenData(orderType)
	const headerFont = fieldTypo(typo, "kitchen_date", "kitchen_order_type_name", "kitchen_order_type").fontSize
	const reasonFont = fieldTypo(typo, "kitchen_comment").fontSize
	const numberFont = fieldTypo(typo, "kitchen_cheque_number").fontSize
	const isCheque = kind === "kitchen_delete_cheque"
	const products = isCheque ? KITCHEN_PRODUCTS : KITCHEN_CHANGED_PRODUCTS

	return (
		<div className="cheque-paper">
			{kitchenHeader({
				flat, typo, t, orderType,
				title: isCheque ? t("check_deleted") : t("delete_in_cheque"),
				titleStyle: { fontSize: headerFont },
				withTable: false,
			})}

			{flat.kitchen_table_info && orderType === ORDER_HALL && (
				<div style={flexRowStyle(typo, "kitchen_table_info")}><div>{t("hall")}: {d.hall_name}</div><div>{t("table")}: {d.table_name}</div></div>
			)}

			<table className="table-bordered-custom" style={{ fontSize: typo.table }}>
				<thead>
					<tr>
						<th className="text-start" style={{ width: "70%" }}>{t("name")}</th>
						<th className="text-center text-nowrap" style={{ width: "30%" }}>{t("quantity")}</th>
					</tr>
				</thead>
				<tbody>
					{products.map((p, i) => (
						<tr key={i}>
							<td>
								<div className="text-start" style={{
									marginLeft: p.modifier ? 10 : 0,
									marginTop: !p.modifier && i > 0 && products[i - 1].modifier ? 8 : 0,
								}}>{p.name}</div>
							</td>
							<td>
								<div className="text-center text-nowrap">
									{isCheque ? p.quantity : <>{p.old_quantity} <span>→</span> <b>{p.new_quantity}</b></>}
								</div>
							</td>
						</tr>
					))}
				</tbody>
			</table>

			{!isCheque && (
				<>
					{hasText(d.deletion_reason_name) && <div style={{ fontSize: reasonFont }}><b className="me-2">{t("deletion_reason")}:</b><span>{d.deletion_reason_name}</span></div>}
					{hasText(d.deletion_reason_comment) && <div style={{ fontSize: reasonFont }}><b className="me-2">{t("comment")}:</b><span>{d.deletion_reason_comment}</span></div>}
				</>
			)}

			<div className={isCheque ? undefined : "mt-2"}>
				<div style={flexRowStyle(typo, "kitchen_employee_info")}><div>{t("created_by")}:</div><div>{d.created_by}</div></div>
				<div style={flexRowStyle(typo, "kitchen_employee_info")}><div>{t("deleted_by")}:</div><div>{d.deleted_by}</div></div>
			</div>

			{isCheque && (
				<>
					{d.deletion_reason_name && <div style={{ fontSize: reasonFont }}><b className="me-2">{t("deletion_reason")}:</b><span>{d.deletion_reason_name}</span></div>}
					{hasText(d.deletion_reason_comment) && <div style={{ fontSize: reasonFont }}><b className="me-2">{t("comment")}:</b><span>{d.deletion_reason_comment}</span></div>}
				</>
			)}

			<div className="pt-2" style={{ width: "100%", textAlign: "center" }}><b style={{ fontSize: numberFont }}>{t("order_number")}</b></div>
			<div style={{ width: "100%", textAlign: "center" }}><b style={{ fontSize: numberFont }}>{d.cheque_number}</b></div>
		</div>
	)
}

// ---------- резерв ----------

function ReservationReceipt({ guest, groups, t }) {
	const flat = buildFlat(groups)
	const g = (groups || []).find((x) => x.value === "reservation")
	const typo = buildTypography(groups, "reservation", "reservation_products_font")
	const textOf = (key, fallback) => (g?.fields || []).find((f) => f.field_key === key)?.text_value || fallback
	// «Гостевой» слип печатает все поля независимо от переключателей
	const on = (key) => (guest ? true : !!flat[key])
	const d = RESERVATION_DATA
	const posNote = textOf("reservation_pos_note", "Примечание ресторана")
	const clientNote = textOf("reservation_client_note", "Принести торт")
	const label = guest ? t("guest") : t("client")
	const showProducts = d.products.length > 0 && (guest || flat.reservation_products !== false)

	return (
		<div className="cheque-paper">
			<div className="text-center"><b style={{ fontSize: 20 }}>{t("reserve")}</b></div>

			{on("reservation_table_info") && (
				<div style={flexRowStyle(typo, "reservation_table_info")}><div>{t("hall")} / {t("table")}</div><div>{d.hall_name} / {d.table_name}</div></div>
			)}
			{on("reservation_datetime") && (
				<>
					<div style={flexRowStyle(typo, "reservation_datetime")}><div>{t("date")}:</div><div>{formatDate(d.reservation_date)}</div></div>
					<div style={flexRowStyle(typo, "reservation_datetime")}>
						<div>{t("time")}:</div>
						<div><span>{d.reservation_start_time}</span> - <span>{d.reservation_end_time}</span></div>
					</div>
				</>
			)}
			{guest && <hr style={{ borderTop: "dashed 1px" }} />}
			{on("reservation_guests_count") && (
				<div style={flexRowStyle(typo, "reservation_guests_count")}><div>{t("guests")}:</div><div>{d.people_number}</div></div>
			)}
			{on("reservation_client_name") && (
				<div style={flexRowStyle(typo, "reservation_client_name")}><div>{label}:</div><div>{d.client_name}</div></div>
			)}
			{on("reservation_client_phone") && (
				<div style={flexRowStyle(typo, "reservation_client_phone")}><div>{t("phone")}:</div><div>{regexPhoneNumber(d.client_phone)}</div></div>
			)}
			{(guest ? Number(d.amount) > 0 : !!flat.reservation_prepaid) && (
				<div style={flexRowStyle(typo, "reservation_prepaid")}><div>{guest ? t("paid") : t("prepayment")}:</div><div>{money(d.amount)}</div></div>
			)}
			{on("reservation_client_note") && hasText(clientNote) && (
				<div style={flexRowStyle(typo, "reservation_client_note")}><div>{t("note")}:</div><div>{clientNote}</div></div>
			)}

			{showProducts && (
				<table
					className={guest ? "custom-cheque-table w-100 fz14" : "table-bordered-custom"}
					style={{ fontSize: typo.table }}>
					{guest && (
						<thead>
							<tr>
								<th style={{ minWidth: 20 }} className="text-center">#</th>
								<th className="text-start w-75"><b>{t("name")}</b></th>
								<th className="text-end text-nowrap w-25"><b>{t("quantity")}</b></th>
							</tr>
						</thead>
					)}
					<tbody>
						{d.products.map((p, i) => (
							<tr key={i}>
								{guest && <td className="text-center">{i + 1}.</td>}
								<td>{p.name}</td>
								<td className={guest ? "text-end text-nowrap" : "text-center text-nowrap"}>{p.quantity}</td>
							</tr>
						))}
					</tbody>
				</table>
			)}

			<hr className="m-0" />

			{on("reservation_user_name") && (
				<div style={flexRowStyle(typo, "reservation_user_name")}><div>{t("employee")}:</div><div>{d.employee_name}</div></div>
			)}
			{on("reservation_pos_phone") && (
				<div style={flexRowStyle(typo, "reservation_pos_phone")}><div>{t("phone")}:</div><div>{regexPhoneNumber(d.pos_phone)}</div></div>
			)}
			{on("reservation_pos_note") && hasText(posNote) && (
				<div style={flexRowStyle(typo, "reservation_pos_note")}><div>{t("note")}:</div><div>{posNote}</div></div>
			)}
		</div>
	)
}

// ---------- переключатели видов ----------

const ORDER_TYPES = [
	{ key: ORDER_HALL, label: "Зал", icon: "uil-restaurant" },
	{ key: ORDER_TAKEAWAY, label: "С собой", icon: "uil-shopping-bag" },
	{ key: ORDER_DELIVERY, label: "Доставка", icon: "uil-truck" },
]

const CHEQUE_KINDS = [
	{ key: "precheck", label: "Пречек", icon: "uil-receipt", orderTypes: true },
	{ key: "payment", label: "Оплата", icon: "uil-money-bill", orderTypes: true },
	{ key: "return", label: "Возврат", icon: "uil-corner-up-left", orderTypes: false },
]

const KITCHEN_KINDS = [
	{ key: "kitchen_new", label: "Создание", icon: "uil-plus-circle", orderTypes: true },
	{ key: "kitchen_delete_products", label: "Правка", title: "Правка / удаление позиций", icon: "uil-edit", orderTypes: true },
	{ key: "kitchen_delete_cheque", label: "Удаление", title: "Удаление чека", icon: "uil-trash-alt", orderTypes: true },
]

const RESERVATION_KINDS = [
	{ key: "reservation", label: "Резерв", icon: "uil-calender", orderTypes: false },
	{ key: "reservation_guest", label: "Резерв гостю", icon: "uil-user", orderTypes: false },
]

const KINDS_BY_GROUP = {
	cheque: CHEQUE_KINDS,
	cheque_products: CHEQUE_KINDS,
	kitchen: KITCHEN_KINDS,
	reservation: RESERVATION_KINDS,
}

/** Поле, которое видно только на конкретном виде чека — при выборе поля переключаем превью туда. */
const FIELD_KIND_HINT = {
	show_feedback_qr: "payment",
	show_loyalty_qr: "payment",
	show_debt_info: "payment",
	show_promo_text_post_cheque: "payment",
	show_cheque_closed_at: "payment",
	show_promo_text_pre_cheque: "precheck",
	kitchen_external_info: "kitchen_new",
	kitchen_sale_price: "kitchen_new",
	kitchen_row_number: "kitchen_new",
}

const FIELD_ORDER_HINT = {
	is_split_by_persons: ORDER_HALL,
	show_hall_name: ORDER_HALL,
	show_table_name: ORDER_HALL,
	show_client_address: ORDER_DELIVERY,
	show_payment_type_name: ORDER_DELIVERY,
	kitchen_external_info: ORDER_DELIVERY,
}

function ChequePreviewPanel({ groups, groupValue, selectedFieldKey }) {
	const { t } = useTranslation()
	const kinds = KINDS_BY_GROUP[groupValue] || CHEQUE_KINDS
	const [kindKey, setKindKey] = useState(kinds[0].key)
	const [orderType, setOrderType] = useState(ORDER_HALL)

	const kind = kinds.find((k) => k.key === kindKey) || kinds[0]

	useEffect(() => {
		const hintKind = FIELD_KIND_HINT[selectedFieldKey]
		if (hintKind && kinds.some((k) => k.key === hintKind)) setKindKey(hintKind)
		const hintOrder = FIELD_ORDER_HINT[selectedFieldKey]
		if (hintOrder) setOrderType(hintOrder)
	}, [selectedFieldKey]) // eslint-disable-line react-hooks/exhaustive-deps

	function renderPaper() {
		switch (kind.key) {
			case "precheck":
			case "payment":
				return <ChequeReceipt stage={kind.key} orderType={orderType} groups={groups} t={t} />
			case "return":
				return <ReturnReceipt t={t} />
			case "kitchen_new":
				return <KitchenNewOrder orderType={orderType} groups={groups} t={t} />
			case "kitchen_delete_products":
			case "kitchen_delete_cheque":
				return <KitchenDeleted kind={kind.key} orderType={orderType} groups={groups} t={t} />
			case "reservation":
				return <ReservationReceipt groups={groups} t={t} />
			case "reservation_guest":
				return <ReservationReceipt guest groups={groups} t={t} />
			default:
				return null
		}
	}

	return (
		<div>
			<div className="cheque-preview-bar">
				<div className="cheque-preview-grid" style={{ gridTemplateColumns: `repeat(${kinds.length}, 1fr)` }}>
					{kinds.map((k) => (
						<button
							key={k.key}
							type="button"
							title={k.title || k.label}
							className={`btn btn-sm ${kind.key === k.key ? "btn-primary" : "btn-outline-primary"}`}
							onClick={() => setKindKey(k.key)}>
							<i className={`uil ${k.icon}`}></i>
							<span>{k.label}</span>
						</button>
					))}
				</div>
				{kind.orderTypes && (
					<div className="cheque-preview-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
						{ORDER_TYPES.map((o) => (
							<button
								key={o.key}
								type="button"
								title={o.label}
								className={`btn btn-sm ${orderType === o.key ? "btn-secondary" : "btn-outline-secondary"}`}
								onClick={() => setOrderType(o.key)}>
								<i className={`uil ${o.icon}`}></i>
								<span>{o.label}</span>
							</button>
						))}
					</div>
				)}
			</div>
			{renderPaper()}
		</div>
	)
}

export default ChequePreviewPanel
